import { useEffect, useState, useMemo } from 'react'
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import CountryISOCode from '../../utils/CountryISOCode.ts'
import { Button } from '../Button.tsx'
import { Input } from '../Input.tsx'

// --- Iconos SVG ---
const EditIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
)

const TrashIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
)

const UserIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
)

const PhoneIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
)

// --- Tipos ---
export interface AddressData {
    id: string
    firstName: string
    lastName: string
    phone: string
    fullAddress: string // String completo para visualización
    copies: string // Added copies field

    // Campos desglosados para modo manual
    formattedAddress: {
        street?: string
        city?: string
        state?: string
        zipCode?: string
        country?: string
    }
    isManual: boolean
}

interface ShippingAddressManagerProps {
    maxAddresses?: number
    maxCopies?: number
    initialAddresses?: AddressData[]
    handleAddressesUpdate?: (addresses: AddressData[]) => void
}

// --- Componente de Formulario Interno ---
const AddressForm = ({
                         initialData,
                         onSave,
                         onCancel,
                         maxAllowedCopies,
                     }: {
    initialData?: AddressData | null
    onSave: (data: AddressData) => void
    onCancel: () => void
    maxAllowedCopies?: number
}) => {
    const { t } = useTranslation()

    // Estados del formulario
    const [formData, setFormData] = useState<Partial<AddressData>>({
        firstName: '',
        lastName: '',
        phone: '',
        fullAddress: '',
        copies: '',
        isManual: false,
        formattedAddress: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: '',
        },
        ...initialData,
    })

    const [googleValue, setGoogleValue] = useState<any>(null)
    const [isLoadingGoogle, setIsLoadingGoogle] = useState(false)

    // Precargar input de Google si editamos una dirección no manual
    useEffect(() => {
        if (initialData && !initialData.isManual && initialData.fullAddress) {
            setGoogleValue({
                label: initialData.fullAddress,})
        }
    }, [initialData])

    const handleGoogleSelect = async (option: any) => {
        setGoogleValue(option)
        if (!option) return

        setIsLoadingGoogle(true)
        try {
            const results = await geocodeByPlaceId(option.value.place_id)
            const placeDetails = results[0]
            const { lat, lng } = placeDetails.geometry.location

            // Helper para extraer componentes
            const getComponent = (types: string[]) =>
                placeDetails.address_components.find((c) =>
                    types.some((t) => c.types.includes(t))
                )

            const cityComponent =
                getComponent(['locality']) || getComponent(['sublocality'])
            const routeComponent = getComponent(['route'])
            const streetNumberComponent = getComponent(['street_number'])
            const countryComponent = getComponent(['country'])
            const stateComponent = getComponent(['administrative_area_level_1'])
            const postalCodeComponent = getComponent(['postal_code'])

            // Construcción inteligente de la calle
            let streetAddress = option.label?.split(cityComponent?.long_name || '')[0].trim()
            // Intentar limpiar la coma final si existe
            if (streetAddress && streetAddress.endsWith(',')) {
                streetAddress = streetAddress.slice(0, -1)
            }

            // Si Google nos da calle y número limpios, los usamos preferentemente
            if (routeComponent) {
                streetAddress = routeComponent.long_name
                if (streetNumberComponent) {
                    streetAddress += ` ${streetNumberComponent.long_name}`
                }
            }

            setFormData((prev) => ({
                ...prev,
                fullAddress: option.label,
                isManual: false,
                // Rellenamos también los campos manuales por si el usuario cambia a manual después
                formattedAddress: {
                    street: streetAddress,
                    city: cityComponent?.long_name || '',
                    state: stateComponent?.long_name || '',
                    zipCode: postalCodeComponent?.long_name || '',
                    country: countryComponent?.short_name
                        ? CountryISOCode.fromCode(countryComponent?.short_name)?.numeric
                        : '',
                },
            }))
        } catch (error) {
            console.error('Error geocoding', error)
        } finally {
            setIsLoadingGoogle(false)
        }
    }

    const handleSubmit = () => {
        // 1. Validaciones básicas
        if (!formData.firstName || !formData.lastName || !formData.phone || !formData.copies) return

        if (maxAllowedCopies !== undefined) {
            const copies = parseInt(formData.copies);
            if (copies > maxAllowedCopies) {
                alert(`You can only add up to ${maxAllowedCopies} copies.`);
                return;
            }
        }

        // 2. Validación y construcción de dirección según el modo
        let finalFullAddress = formData.fullAddress
        if (formData.isManual) {
            // Validar que todos los campos manuales estén llenos
            if (
                !formData.formattedAddress?.street ||
                !formData.formattedAddress?.city ||
                !formData.formattedAddress?.zipCode ||
                !formData.formattedAddress?.country
            ) {
                // Podrías mostrar un error visual aquí
                return
            }
            // Construimos el string completo para mostrarlo en la lista
            finalFullAddress = `${formData.formattedAddress.street}, ${formData.formattedAddress.zipCode} ${formData.formattedAddress.city}, ${formData.formattedAddress?.state ? formData.formattedAddress.state + ', ' : ''}${formData.formattedAddress.country}`
        } else {
            if (!formData.fullAddress) return
        }

        onSave({
            id: initialData?.id || uuidv4(),
            firstName: formData.firstName!,
            lastName: formData.lastName!,
            phone: formData.phone!,
            copies: formData.copies!,
            fullAddress: finalFullAddress!,
            isManual: formData.isManual || false,
            formattedAddress: formData.formattedAddress!,
        })
    }

    // Comprobar si el botón debe estar deshabilitado
    const isSaveDisabled =
        !formData.firstName ||
        !formData.lastName ||
        !formData.phone ||
        !formData.copies ||
        (formData.isManual
            ? !formData.formattedAddress?.street ||
              !formData.formattedAddress?.city ||
              !formData.formattedAddress?.zipCode ||
              !formData.formattedAddress?.country
            : !formData.fullAddress)

    return (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <h4 className="font-bold text-gray-800">
                {t(
                    initialData
                        ? 'onboarding.shippingAddressEditAddress'
                        : 'onboarding.shippingAddressNewAddress',
                    { ns: 'invitations', defaultValue: 'Add Address' }
                )}
            </h4>

            {/* Campos de Nombre y Teléfono */}
            <div className="flex gap-4 flex-wrap">
                <div className="flex-1 ">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {t('onboarding.shippingAddressName', { ns: 'invitations', defaultValue: 'First Name' })}
                    </label>
                    <Input
                        type="text"
                        className="w-full text-sm bg-neutralBlack-50"
                        size="small"
                        value={formData.firstName}
                        onChange={(e) =>
                            setFormData({ ...formData, firstName: e.target.value })
                        }
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {t('onboarding.shippingAddressLastName', { ns: 'invitations', defaultValue: 'Last Name' })}
                    </label>
                    <Input
                        type="text"
                        className="w-full text-sm bg-neutralBlack-50"
                        size="small"
                        value={formData.lastName}
                        onChange={(e) =>
                            setFormData({ ...formData, lastName: e.target.value })
                        }
                    />
                </div>
            </div>

            <div className="flex gap-4">
                 <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {t('onboarding.shippingAddressPhone', { ns: 'invitations', defaultValue: 'Phone' })}
                    </label>
                    <input
                        type="tel"
                        className="w-full p-2   rounded-lg text-sm  bg-neutralBlack-50"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+34 600 123 456"
                    />
                </div>
                <div className="w-24">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Copies
                    </label>
                    <input
                        type="number"
                        className="w-full p-2   rounded-lg text-sm bg-neutralBlack-50 text-center"
                        value={formData.copies}
                        onChange={(e) => setFormData({ ...formData, copies: e.target.value })}
                        placeholder="0"
                    />
                    {maxAllowedCopies !== undefined && (
                        <div className="text-[10px] text-gray-500 mt-1">
                            Max: {maxAllowedCopies}
                        </div>
                    )}
                </div>
            </div>

            {/* Selector de Dirección */}
            <div>
                <div className="flex justify-between items-center mb-1">
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('onboarding.shippingAddressFullAddress', { ns: 'invitations', defaultValue: 'Full Address' })}
                    </label>
                    <button
                        type="button"
                        onClick={() => {
                            const newIsManual = !formData.isManual
                            // Si pasamos a manual y no hay datos, limpiamos. Si había datos de google, los mantenemos en los inputs
                            setFormData((prev) => ({
                                ...prev,
                                isManual: newIsManual,
                                fullAddress: newIsManual ? '' : prev.fullAddress,
                            }))
                            if (!newIsManual) setGoogleValue(null)
                        }}
                        className="text-xs text-blue-600 underline hover:text-blue-800"
                    >
                        {t(
                            formData.isManual
                                ? 'onboarding.shippingAddressSearch'
                                : 'onboarding.shippingAddressManually',
                            { ns: 'invitations', defaultValue: formData.isManual ? 'Use Google Search' : 'Enter Manually' }
                        )}
                    </button>
                </div>

                {formData.isManual ? (
                    <div className="space-y-2">
                        {/* Calle */}
                        <div>
                            <label className="block text-xs text-gray-500">
                                {t('onboarding.streetNumber', { ns: 'invitations', defaultValue: 'Street & Number' })}
                            </label>
                            <Input
                                type="text"
                                className="w-full text-sm bg-neutralBlack-50"
                                size="small"
                                placeholder=""
                                value={formData.formattedAddress?.street}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        formattedAddress: {
                                            ...formData.formattedAddress,
                                            street: e.target.value,
                                        },
                                    })
                                }
                            />
                        </div>

                        {/* Ciudad y CP */}
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <label className="block text-xs text-gray-500">
                                    {t('onboarding.postcode', { ns: 'invitations', defaultValue: 'Postal Code' })}
                                </label>
                                <Input
                                    type="text"
                                    className="w-full text-sm bg-neutralBlack-50"
                                    size="small"
                                    placeholder=""
                                    value={formData.formattedAddress?.zipCode}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            formattedAddress: {
                                                ...formData.formattedAddress,
                                                zipCode: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs text-gray-500">
                                    {' '}
                                    {t('onboarding.city', { ns: 'invitations', defaultValue: 'City' })}
                                </label>
                                <Input
                                    type="text"
                                    className="w-full text-sm bg-neutralBlack-50"
                                    size="small"
                                    placeholder=""
                                    value={formData.formattedAddress?.city}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            formattedAddress: {
                                                ...formData.formattedAddress,
                                                city: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </div>
                        </div>

                        {/* Provincia y País */}
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <label className="block text-xs text-gray-500">
                                    {' '}
                                    {t('onboarding.state', { ns: 'invitations', defaultValue: 'State/Province' })}
                                </label>
                                <Input
                                    type="text"
                                    className="w-full text-sm bg-neutralBlack-50"
                                    size="small"
                                    placeholder=""
                                    value={formData.formattedAddress?.state}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            formattedAddress: {
                                                ...formData.formattedAddress,
                                                state: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs text-gray-500">
                                    {' '}
                                    {t('onboarding.country', { ns: 'invitations', defaultValue: 'Country' })}
                                </label>
                                <Input
                                    type="text"
                                    className="w-full text-sm bg-neutralBlack-50"
                                    size="small"
                                    placeholder=""
                                    value={formData.formattedAddress?.country}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            formattedAddress: {
                                                ...formData.formattedAddress,
                                                country: e.target.value,
                                            },
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <GooglePlacesAutocomplete
                            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                            selectProps={{
                                value: googleValue,
                                onChange: handleGoogleSelect,
                                isLoading: isLoadingGoogle,
                                placeholder: t('onboarding.shippingAddressYourAddress', {
                                    ns: 'invitations', defaultValue: 'Start typing your address...'
                                }),
                                styles: {
                                    indicatorSeparator: () => ({ display: 'none' }),
                                    control: (provided) => ({
                                        ...provided,
                                        fontSize: '0.875rem',
                                        borderColor: '#e5e7eb',
                                        boxShadow: 'none',
                                        '&:hover': { borderColor: '#d1d5db' },
                                        borderRadius: '0.5rem',
                                        padding: '2px',
                                        backgroundColor: '#F9FAFB'
                                    }),
                                },
                            }}
                            autocompletionRequest={{}}
                        />
                        {isLoadingGoogle && <span className="text-xs text-gray-400">Loading...</span>}
                    </div>
                )}
            </div>

            {/* Botones de Acción */}
            <div className="flex justify-end gap-2 pt-2">
                <Button variant={'secondary'} size={'small'} onClick={onCancel}>
                    {t('onboarding.shippingAddressCancel', { ns: 'invitations', defaultValue: 'Cancel' })}
                </Button>
                <Button size={'small'} onClick={handleSubmit} disabled={isSaveDisabled}>
                    {t(
                        initialData
                            ? 'onboarding.shippingAddressSaving'
                            : 'onboarding.shippingAddressAction',
                        { ns: 'invitations', defaultValue: 'Save Address' }
                    )}
                </Button>
            </div>
        </div>
    )
}

// --- Componente Principal ---
export const ShippingAddressManager: React.FC<ShippingAddressManagerProps> = ({
                                                                                  maxAddresses = 1,
                                                                                  maxCopies,
                                                                                  initialAddresses = [],
                                                                                  handleAddressesUpdate,
                                                                              }) => {
    const { t } = useTranslation()
    const [addresses, setAddresses] = useState<AddressData[]>(initialAddresses)
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)

    useEffect(() => {
        setAddresses(initialAddresses)
    }, [initialAddresses])

    const usedCopies = useMemo(() => {
        return addresses.reduce((sum, addr) => sum + (parseInt(addr.copies || '0') || 0), 0)
    }, [addresses])

    const handleSave = (data: AddressData) => {
        let updatedList
        if (editingId) {
            updatedList = addresses.map((addr) => (addr.id === editingId ? data : addr))
        } else {
            updatedList = [...addresses, data]
        }
        setAddresses(updatedList)
        if (handleAddressesUpdate) handleAddressesUpdate(updatedList)
        setIsFormOpen(false)
        setEditingId(null)
    }

    const handleDelete = (id: string) => {
        if (!window.confirm('Are you sure you want to delete this address?')) return
        const updatedList = addresses.filter((a) => a.id !== id)
        setAddresses(updatedList)
        if (handleAddressesUpdate) handleAddressesUpdate(updatedList)
    }

    const openEdit = (addr: AddressData) => {
        setEditingId(addr.id)
        setIsFormOpen(true)
    }

    const openNew = () => {
        setEditingId(null)
        setIsFormOpen(true)
    }

    return (
        <div className="space-y-4">
            {/* Cabecera */}
            <div className="flex justify-between items-center">
                {maxCopies !== undefined ? (
                    <div className="text-sm text-gray-500">
                        <span>
                            {usedCopies} / {maxCopies} copies assigned
                        </span>
                    </div>
                ) : (maxAddresses > 1 && (
                    <div className="text-sm text-gray-500">
                        {/* {t('onboarding.shippingAddressHeaderPlural', { ns: 'invitations' })} */}
                        <span>
              {addresses.length} / {maxAddresses} addresses
            </span>
                    </div>
                ))}
            </div>

            {/* Lista de Direcciones */}
            {!isFormOpen && (
                <div className="space-y-3">
                    {addresses.map((addr) => (
                        <div
                            key={addr.id}
                            className="bg-white p-4 rounded-lg  flex justify-between items-start"
                        >
                            <div className="space-y-1">
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                    <UserIcon /> {addr.firstName} {addr.lastName}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <PhoneIcon /> {addr.phone}
                                </div>
                                <div className="text-xs text-gray-700 mt-1">
                                    {/* Mostramos la dirección completa construida */}
                                    {addr.fullAddress}
                                </div>
                                {addr.isManual && (
                                    <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1 rounded">
                    Manual
                  </span>
                                )}
                                <div className="text-xs font-semibold text-gray-800 mt-1">
                                    {addr.copies} Copies
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => openEdit(addr)}
                                    className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-full transition-colors"
                                    title={t('edit', {ns: 'dashboard'})}
                                >
                                    <EditIcon />
                                </button>
                                <button
                                    onClick={() => handleDelete(addr.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                    title={t('delete', {ns: 'dashboard'})}
                                >
                                    <TrashIcon />
                                </button>
                            </div>
                        </div>
                    ))}

                    {/* Botón Añadir */}
                    {(maxCopies === undefined || usedCopies < maxCopies) && (maxAddresses === undefined || addresses.length < maxAddresses) && (
                        <button
                            onClick={openNew}
                            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:text-black hover:border-gray-400 hover:bg-gray-50 transition-all text-sm font-medium flex items-center justify-center gap-2"
                        >
                            <span>+</span>
                            {/* {t('onboarding.shippingAddressAddNewAddress', { ns: 'invitations' })} */}
                            Add New Address
                        </button>
                    )}
                </div>
            )}

            {/* Formulario */}
            {isFormOpen && (
                <AddressForm
                    initialData={editingId ? addresses.find((a) => a.id === editingId) : null}
                    onSave={handleSave}
                    onCancel={() => {
                        setIsFormOpen(false)
                        setEditingId(null)
                    }}
                    maxAllowedCopies={maxCopies !== undefined ? (maxCopies - usedCopies + (editingId ? (parseInt(addresses.find((a) => a.id === editingId)?.copies || '0') || 0) : 0)) : undefined}
                />
            )}
        </div>
    )
}

